import { type ReactNode } from 'react'
import { Head } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatDollars } from '@/lib/format'
import type { OrgSignup, UserSignup, MostViewedEvent } from '@/types'

type Props = {
  stats: {
    users: number
    organizations: number
    total_events: number
    upcoming_events: number
    past_events: number
    page_views: number
    gross_revenue_cents: number
    net_revenue_cents: number
    processing_fee_cents: number
  }
  org_signups: OrgSignup[]
  user_signups: UserSignup[]
  most_viewed_events: MostViewedEvent[]
}

function StatCard({ label, value, currency = false }: { label: string; value: number; currency?: boolean }) {
  return (
    <div className="rounded-2xl bg-muted/30 ring-1 ring-black/5 p-6">
      <p className="text-base text-muted-foreground font-medium">{label}</p>
      <p className="mt-1 text-4xl font-semibold tracking-tight">
        {currency ? formatDollars(value) : value.toLocaleString()}
      </p>
    </div>
  )
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return 'Fundraiser'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function AdminDashboard({ stats, org_signups, user_signups, most_viewed_events }: Props) {
  const recentOrgSignups = org_signups.filter(d => d.total > 0).slice(-14)
  const recentUserSignups = user_signups.filter(d => d.count > 0).slice(-14)

  return (
    <>
      <Head title="Admin Dashboard" />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Dashboard</h1>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Users" value={stats.users} />
          <StatCard label="Organizations" value={stats.organizations} />
          <StatCard label="Total Events" value={stats.total_events} />
          <StatCard label="Upcoming Events" value={stats.upcoming_events} />
          <StatCard label="Past Events" value={stats.past_events} />
          <StatCard label="Page Views" value={stats.page_views} />
          <StatCard label="Gross Revenue" value={stats.gross_revenue_cents} currency />
          <StatCard label="Net Revenue" value={stats.net_revenue_cents} currency />
          <StatCard label="Card Processing Fees" value={stats.processing_fee_cents} currency />
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Org Signups */}
          <div>
            <h2 className="text-xl font-semibold tracking-tight font-serif">Org Signups (Last 30 Days)</h2>
            {recentOrgSignups.length > 0 ? (
              <Table className="mt-4">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Free</TableHead>
                    <TableHead className="text-right">Pro</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentOrgSignups.map(day => (
                    <TableRow key={day.date}>
                      <TableCell>{formatDate(day.date)}</TableCell>
                      <TableCell className="text-right">{day.free}</TableCell>
                      <TableCell className="text-right">{day.pro}</TableCell>
                      <TableCell className="text-right font-medium">{day.total}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="mt-4 text-base text-muted-foreground">No org signups in the last 30 days.</p>
            )}
          </div>

          {/* User Signups */}
          <div>
            <h2 className="text-xl font-semibold tracking-tight font-serif">User Signups (Last 30 Days)</h2>
            {recentUserSignups.length > 0 ? (
              <Table className="mt-4">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentUserSignups.map(day => (
                    <TableRow key={day.date}>
                      <TableCell>{formatDate(day.date)}</TableCell>
                      <TableCell className="text-right font-medium">{day.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="mt-4 text-base text-muted-foreground">No user signups in the last 30 days.</p>
            )}
          </div>
        </div>

        {/* Most Viewed Events */}
        <div className="mt-10">
          <h2 className="text-xl font-semibold tracking-tight font-serif">Most Viewed Events</h2>
          {most_viewed_events.length > 0 ? (
            <Table className="mt-4">
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {most_viewed_events.map(event => (
                  <TableRow key={event.id}>
                    <TableCell className="font-medium">{event.title}</TableCell>
                    <TableCell>{event.organization_name}</TableCell>
                    <TableCell>{formatDate(event.date)}</TableCell>
                    <TableCell className="text-right">{event.view_count.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="mt-4 text-base text-muted-foreground">No event views recorded yet.</p>
          )}
        </div>
      </div>
    </>
  )
}

AdminDashboard.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
